// Pure, reusable field registry for the winder AI enrichment surfaces — the
// per-winder review diff in CustomWinderModal and the bulk review grid in
// BulkWinderAIEnhance.
//
// This is the winder analogue of calibreEnrichFields.ts. Two shape differences
// drive everything below:
//
//  1. `enrichWinder` returns a PER-FIELD result map (`{ slotCount: { value,
//     confidence, reasoning }, … }`), like `enrichElectronicModule` and unlike
//     `enrichCalibre`'s single overall confidence. So confidence is read per
//     cell, not per row.
//  2. `WinderEntry` is FLAT — no `data.*` sub-map like CalibreData — so a patch
//     key is just the field name. No dotted paths, no spec→data remaps.
//
// Every function here is pure: it reads a stored `WinderEntry` and a result
// object, and returns plain values. No component state, so the same code backs
// the single-winder apply and the headless bulk write.

import type { StorageApplianceKind, WinderEntry } from '../types';
import {
  WINDER_ONLY_KEYS,
  resolveApplianceKind,
} from './winderApplianceKind';
import type { WinderEnrichResult } from './firebase';

export type WinderFieldType =
  | 'str'
  | 'int'
  | 'bool'
  | 'strArr'
  | 'intArr'
  | 'range'
  /**
   * The `StorageApplianceKind` union — its own type rather than a `str`,
   * because a value outside {winder, safe} must be DROPPED rather than written.
   * It decides which half of the editor renders, so an unexpected string would
   * open the form in a state neither branch handles.
   */
  | 'kind';

export interface WinderEnrichFieldSpec {
  /** Key as it appears in the enrichWinder result map AND on WinderEntry. */
  key: string;
  /** Human label — column header in the review grid, row label in the diff. */
  label: string;
  type: WinderFieldType;
  /**
   * Sub-field of `safeSpecs` this key stands for.
   *
   * The nine security specs are stored NESTED (`safeSpecs.certification`) but
   * reviewed FLAT — one cell each. The model returns them as a single
   * `safeSpecs` envelope, so all nine read out of that one result entry and
   * share its confidence, while each writes its own dotted path.
   *
   * Flat review is the point: `certification` and `insuranceCashRating` are the
   * two most confabulation-prone fields here, and a reviewer has to be able to
   * take the weight and reject the insurance rating. One all-or-nothing cell
   * would make accepting a hallucinated rating the price of accepting anything.
   */
  safeSpecKey?: string;
  /**
   * Sub-field of `displayCaseSpecs` this key stands for — the exact twin of
   * `safeSpecKey`, one block over. Nested storage, flat review, for the same
   * reasons.
   */
  displayCaseSpecKey?: string;
}

/** The `safeSpecs` sub-field a flattened key stands for, or undefined. */
const SAFE_SPEC_PARENT = 'safeSpecs';

/** The `displayCaseSpecs` sub-field a flattened key stands for, or undefined. */
const DISPLAY_CASE_SPEC_PARENT = 'displayCaseSpecs';

// Order here is the column order in the review grid and the row order in the
// single-winder apply diff. Grouped identity → capacity/motion → build →
// positioning → provenance, matching the editor's section order so a reviewer
// reads the grid in the same order they'd read the form.
const SPEC_LIST: WinderEnrichFieldSpec[] = [
  // shared-types v1.101.0. First in the list because it is the question every
  // other answer hangs off — a reviewer reading the grid left to right should
  // learn WHAT the thing is before reading how many turns per day it does.
  { key: 'applianceKind', label: 'Appliance kind', type: 'kind' },
  { key: 'displayName', label: 'Display name', type: 'str' },
  { key: 'countryOfManufacture', label: 'Country of manufacture', type: 'str' },
  { key: 'slotCount', label: 'Slots', type: 'int' },
  { key: 'turnsPerDay', label: 'Turns per day', type: 'intArr' },
  { key: 'rotationDirection', label: 'Rotation direction', type: 'str' },
  { key: 'rotationProgramSlugs', label: 'Programs', type: 'strArr' },
  { key: 'powerSourceSlugs', label: 'Power sources', type: 'strArr' },
  { key: 'materialSlugs', label: 'Materials', type: 'strArr' },
  { key: 'isQuiet', label: 'Quiet operation', type: 'bool' },
  { key: 'hasLock', label: 'Lockable', type: 'bool' },
  { key: 'hasDisplayCase', label: 'Display case', type: 'bool' },
  { key: 'priceTierSlug', label: 'Price tier', type: 'str' },
  { key: 'aliases', label: 'Also known as', type: 'strArr' },
  { key: 'sourceUrls', label: 'Source URLs', type: 'strArr' },
  // shared-types v1.97.0 additions.
  { key: 'turnsPerDayRange', label: 'TPD range', type: 'range' },
  { key: 'startDelayMaxHours', label: 'Start delay (h)', type: 'int' },
  { key: 'isSafe', label: 'Is a safe', type: 'bool' },
  // The ten flattened `safeSpecs` cells. Only ever populated for a safe, so
  // for an ordinary winder these columns simply never appear — `suggestedWinderKeys`
  // drops a key the model answered with nothing.
  {
    key: 'safeCertification',
    label: 'Certification',
    type: 'str',
    safeSpecKey: 'certification',
  },
  {
    key: 'safeFireRatingMinutes',
    label: 'Fire rating (min)',
    type: 'int',
    safeSpecKey: 'fireRatingMinutes',
  },
  /*
   * The two fire temperatures, exposure first so the pair reads the way it is
   * spoken: "60 minutes at 927°C, interior below 177°C".
   *
   * Both labels name their half explicitly. The old one — "Fire max temp (°C)"
   * — was ambiguous between them, and against a value of 177 it read as a
   * feeble rating when it is in fact the PROTECTED figure. A curator cannot
   * check a number they cannot tell the meaning of.
   */
  {
    key: 'safeFireExternalTempC',
    label: 'Fire exposure (°C)',
    type: 'int',
    safeSpecKey: 'fireExternalTempC',
  },
  {
    key: 'safeFireMaxInternalTempC',
    label: 'Interior max (°C)',
    type: 'int',
    safeSpecKey: 'fireMaxInternalTempC',
  },
  { key: 'safeWeightKg', label: 'Weight (kg)', type: 'int', safeSpecKey: 'weightKg' },
  {
    key: 'safeLockingMechanism',
    label: 'Locking',
    type: 'str',
    safeSpecKey: 'lockingMechanism',
  },
  {
    key: 'safeLockingBoltCount',
    label: 'Locking bolts',
    type: 'int',
    safeSpecKey: 'lockingBoltCount',
  },
  {
    key: 'safeWallThicknessMm',
    label: 'Wall thickness (mm)',
    type: 'int',
    safeSpecKey: 'wallThicknessMm',
  },
  {
    key: 'safeHasAnchorPoints',
    label: 'Anchor points',
    type: 'bool',
    safeSpecKey: 'hasAnchorPoints',
  },
  {
    key: 'safeInsuranceCashRating',
    label: 'Insurance rating',
    type: 'str',
    safeSpecKey: 'insuranceCashRating',
  },
  // The seven flattened `displayCaseSpecs` cells, on the same footing as the
  // safe ones above: the model returns a single `displayCaseSpecs` envelope, so
  // all seven read out of that one result entry and share its confidence, while
  // each writes its own dotted path. Flat review matters for the same reason —
  // a reviewer has to be able to take the glazing and reject the UV claim,
  // which is the most confabulation-prone field here.
  {
    key: 'displayGlazing',
    label: 'Glazing',
    type: 'str',
    displayCaseSpecKey: 'glazing',
  },
  {
    key: 'displayHasUvFiltering',
    label: 'UV filtering',
    type: 'bool',
    displayCaseSpecKey: 'hasUvFiltering',
  },
  {
    key: 'displayHasIllumination',
    label: 'Illumination',
    type: 'bool',
    displayCaseSpecKey: 'hasIllumination',
  },
  {
    key: 'displayHasHygrometer',
    label: 'Hygrometer',
    type: 'bool',
    displayCaseSpecKey: 'hasHygrometer',
  },
  {
    key: 'displayHasHumidityControl',
    label: 'Humidity control',
    type: 'bool',
    displayCaseSpecKey: 'hasHumidityControl',
  },
  {
    key: 'displayMountingStyle',
    label: 'Mounting style',
    type: 'str',
    displayCaseSpecKey: 'mountingStyle',
  },
  {
    key: 'displayHasDrawer',
    label: 'Drawer',
    type: 'bool',
    displayCaseSpecKey: 'hasDrawer',
  },
];

/**
 * Pseudo-key for the description column.
 *
 * A pseudo-key rather than a plain spec entry because description is the one
 * ALWAYS-APPLY field (below) — the registry has to be able to tell it apart
 * from an ordinary spec cell without string-matching a real field name.
 * It still reads from and writes to the literal `description` field.
 */
export const WINDER_DESCRIPTION_KEY = '__description__';

/** The Firestore field the description pseudo-key reads and writes. */
const DESCRIPTION_FIELD = 'description';

/**
 * Fields that ignore the fill/overwrite write-mode and default to checked even
 * when the winder already has a value.
 *
 * Only `description` qualifies. There is deliberately NO canonical-name
 * equivalent of `CALIBRE_NAME_KEY` here: a winder's identity is `make` + `model`
 * and those two feed the doc id, so an AI rename would silently orphan every
 * `UserWinder.winderCatalogId` FK pointing at the doc. The prompt is told to
 * treat make as ground truth and the model is corrected by hand in the editor.
 */
export const WINDER_ALWAYS_APPLY_KEYS = new Set<string>([WINDER_DESCRIPTION_KEY]);

export const WINDER_ENRICH_FIELDS: Record<string, WinderEnrichFieldSpec> =
  Object.fromEntries(SPEC_LIST.map((s) => [s.key, s]));

/** Column order in the review grid, with the (long) description column last. */
export const WINDER_ENRICH_FIELD_ORDER: string[] = [
  ...SPEC_LIST.map((s) => s.key),
  WINDER_DESCRIPTION_KEY,
];

export function getWinderFieldSpec(key: string): WinderEnrichFieldSpec | undefined {
  return WINDER_ENRICH_FIELDS[key];
}

export function getWinderFieldLabel(key: string): string {
  if (key === WINDER_DESCRIPTION_KEY) return 'Description';
  return WINDER_ENRICH_FIELDS[key]?.label ?? key;
}

// ── coercion ────────────────────────────────────────────────────────────────
// Defensive per-type: a value the model returns should apply whether it arrives
// as the declared type, a string that parses to it, or a single-element array.
// Each returns null when the value can't be coerced, and the caller drops the
// cell rather than writing a junk value.

function toStr(v: unknown): string | null {
  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' ? null : t;
  }
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

function toInt(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.round(v) : null;
  if (typeof v === 'string') {
    const n = parseFloat(v.trim());
    return Number.isFinite(n) ? Math.round(n) : null;
  }
  return null;
}

function toBool(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  // The tool schema types these as booleans, but a model that answers in prose
  // ("yes") would otherwise drop the cell silently.
  if (typeof v === 'string') {
    const t = v.trim().toLowerCase();
    if (t === 'true' || t === 'yes') return true;
    if (t === 'false' || t === 'no') return false;
  }
  return null;
}

function toStrArr(v: unknown): string[] | null {
  const raw = Array.isArray(v) ? v : typeof v === 'string' ? v.split(/[,;]+/) : null;
  if (!raw) return null;
  const parts = raw
    .map((x) => (typeof x === 'string' ? x.trim() : x == null ? '' : String(x).trim()))
    .filter(Boolean);
  return parts.length ? parts : null;
}

function toIntArr(v: unknown): number[] | null {
  const raw = Array.isArray(v) ? v : typeof v === 'string' ? v.split(/[,;]+/) : [v];
  const parts = raw
    .map((x) => toInt(x))
    .filter((n): n is number => n !== null && n > 0);
  if (!parts.length) return null;
  // Deduped + ascending, matching what saveWinder persists — so a bulk write and
  // an editor save produce byte-identical turnsPerDay arrays.
  return [...new Set(parts)].sort((a, b) => a - b);
}

/** Coerce a raw suggestion into the field's stored shape, or null if unusable. */
export function coerceWinderValue(key: string, value: unknown): unknown {
  if (key === WINDER_DESCRIPTION_KEY) return toStr(value);
  const spec = WINDER_ENRICH_FIELDS[key];
  if (!spec) return null;
  switch (spec.type) {
    case 'str':
      return toStr(value);
    case 'int':
      return toInt(value);
    case 'bool':
      return toBool(value);
    case 'strArr':
      return toStrArr(value);
    case 'intArr':
      return toIntArr(value);
    case 'range':
      return toRange(value);
    case 'kind':
      return toApplianceKind(value);
  }
}

/**
 * A `StorageApplianceKind`, or null when the model answered with anything else.
 *
 * Strict on purpose, and the same validation `buildWinderIndexRecord` applies on
 * the way into Algolia: a free-text "winder/safe combo" reaching the document
 * would put the editor and the RN picker into a state neither has a branch for.
 * Tolerant only of case and surrounding space, which is presentation rather
 * than a different answer.
 */
function toApplianceKind(v: unknown): StorageApplianceKind | null {
  const s = toStr(v);
  if (!s) return null;
  const t = s.toLowerCase();
  return t === 'winder' || t === 'safe' || t === 'display_case' ? t : null;
}

/**
 * A `{ min, max }` span, or null. Both bounds must be usable and the right way
 * round: a half-formed range reaching the document opens the editor in range
 * mode with nothing to show, and the next save clears the settings list it
 * displaced.
 */
function toRange(v: unknown): { min: number; max: number } | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const min = toInt((v as Record<string, unknown>).min);
  const max = toInt((v as Record<string, unknown>).max);
  if (min === null || max === null) return null;
  if (min <= 0 || min > max) return null;
  return { min, max };
}

/**
 * Any winder value — stored or proposed — as a display string.
 *
 * THE one formatter for every winder enrichment surface: the review panel, the
 * apply-review diff and the bulk grid all route through here. They each used to
 * carry their own copy, and every copy ended `String(value)`, so the nested
 * fields shared-types v1.97.0 added (`turnsPerDayRange`, and the `safeSpecs`
 * envelope before it was flattened) rendered as a literal "[object Object]" in
 * front of the curator. A single implementation is also what keeps the diff's
 * "current" and "proposed" columns comparable: they are compared as STRINGS to
 * decide `changed`, so two formatters that disagree on the separator would mark
 * every range as changed.
 *
 * '' means "nothing to show", which is what "fill empty fields only" keys off —
 * so a stored boolean `false` must render as 'No' rather than '', or a
 * fill-empty run would flip a deliberate "not quiet" to the AI's guess. Mirrors
 * `dispBool` in bulkEnrichFields and `getCalibreExisting`.
 */
export function formatWinderValue(v: unknown): string {
  if (v === undefined || v === null) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean).join(', ');
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  if (typeof v === 'object') {
    // A range reads as its bounds. Plain '-', not an en dash: `getWinderExisting`
    // has always produced this and the diff string-compares the two columns.
    const r = v as { min?: unknown; max?: unknown };
    if (typeof r.min === 'number' && typeof r.max === 'number') {
      return `${r.min}-${r.max}`;
    }
    // Defensive, and deliberately not ''. Every object-valued field the registry
    // knows about is either a range or flattened into scalar cells, so nothing
    // should reach here — but a future nested field that does will show its
    // contents rather than silently reading as empty (which the diff would take
    // for "no proposal") or as "[object Object]".
    const parts = Object.entries(v as Record<string, unknown>)
      .filter(([, sub]) => sub !== null && sub !== undefined && sub !== '')
      .map(([subKey, sub]) => `${subKey}: ${formatWinderValue(sub)}`);
    return parts.join(', ');
  }
  return '';
}

/**
 * The winder's current value for a field as a display string. '' means "no
 * existing value", which is what "fill empty fields only" keys off.
 */
export function getWinderExisting(entry: WinderEntry, key: string): string {
  const spec = WINDER_ENRICH_FIELDS[key];
  const field = key === WINDER_DESCRIPTION_KEY ? DESCRIPTION_FIELD : key;
  const v = spec?.safeSpecKey
    ? ((entry.safeSpecs ?? undefined) as Record<string, unknown> | undefined)?.[
        spec.safeSpecKey
      ]
    : (entry as unknown as Record<string, unknown>)[field];
  return formatWinderValue(v);
}

/**
 * Read the proposed value for one field out of an enrichWinder result.
 *
 * The callable puts multi-value fields under `values` (plural) and scalars under
 * `value`; falling back across both keeps a scalar-shaped entry for an array
 * field (or vice versa) usable rather than silently dropped — the coercion above
 * normalises either shape.
 */
export function getWinderSuggestion(result: WinderEnrichResult, key: string): unknown {
  const spec = WINDER_ENRICH_FIELDS[key];
  // The model answers the nine security specs as ONE `safeSpecs` envelope, so
  // every flattened key reads its own sub-field out of that single entry.
  if (spec?.safeSpecKey) {
    const block = result[SAFE_SPEC_PARENT];
    const value = block?.value;
    if (!value || typeof value !== 'object') return undefined;
    return (value as Record<string, unknown>)[spec.safeSpecKey];
  }
  // The seven display cells read out of their own single envelope, the same way.
  if (spec?.displayCaseSpecKey) {
    const block = result[DISPLAY_CASE_SPEC_PARENT];
    const value = block?.value;
    if (!value || typeof value !== 'object') return undefined;
    return (value as Record<string, unknown>)[spec.displayCaseSpecKey];
  }
  const field = key === WINDER_DESCRIPTION_KEY ? DESCRIPTION_FIELD : key;
  const entry = result[field];
  if (!entry || typeof entry !== 'object') return undefined;
  return entry.values ?? entry.value;
}

/** Per-field confidence from an enrichWinder result; 'low' when absent. */
export function getWinderConfidence(result: WinderEnrichResult, key: string): string {
  const spec = WINDER_ENRICH_FIELDS[key];
  // All nine security cells share the envelope's confidence — the model rated
  // the block, not each sub-field.
  const field = spec?.safeSpecKey
    ? SAFE_SPEC_PARENT
    : spec?.displayCaseSpecKey
      ? DISPLAY_CASE_SPEC_PARENT
      : key === WINDER_DESCRIPTION_KEY
        ? DESCRIPTION_FIELD
        : key;
  const c = result[field]?.confidence;
  return typeof c === 'string' && c ? c.toLowerCase() : 'low';
}

/**
 * Per-field reasoning from an enrichWinder result; '' when absent.
 *
 * Resolves the same way `getWinderConfidence` does, so all nine security cells
 * report the one envelope's reasoning. The panel renders a repeated string once
 * rather than nine times — see `AiWinderEnrichPanel`.
 */
export function getWinderReasoning(result: WinderEnrichResult, key: string): string {
  const spec = WINDER_ENRICH_FIELDS[key];
  const field = spec?.safeSpecKey
    ? SAFE_SPEC_PARENT
    : spec?.displayCaseSpecKey
      ? DISPLAY_CASE_SPEC_PARENT
      : key === WINDER_DESCRIPTION_KEY
        ? DESCRIPTION_FIELD
        : key;
  const r = result[field]?.reasoning;
  return typeof r === 'string' ? r.trim() : '';
}

/**
 * Every field key this result actually proposes a usable value for, in registry
 * order. Drives which columns the review grid renders — a field the model
 * omitted (or answered unusably) never becomes a column.
 */
export function suggestedWinderKeys(result: WinderEnrichResult): string[] {
  return WINDER_ENRICH_FIELD_ORDER.filter((key) => {
    const raw = getWinderSuggestion(result, key);
    if (raw === undefined) return false;
    const coerced = coerceWinderValue(key, raw);
    return coerced !== null && coerced !== undefined;
  });
}

/**
 * Build the Firestore patch for a set of accepted cells of a single winder.
 *
 * `WinderEntry` is flat, so each accepted key maps to one top-level field. Keys
 * whose value can't be coerced are skipped, so `{}` back means "nothing usable
 * was accepted" and the caller should skip the write entirely rather than
 * stamping a no-op `updatedAt`.
 *
 * `make`, `model` and `makeSlug` are NOT writable here by construction — they
 * aren't in the registry. They feed the doc id, so changing them belongs in the
 * editor (which keeps the existing id), never in a bulk apply.
 *
 * `_entry` is unused: unlike `buildCalibrePatch`, which reads the entry to demote
 * the old name into the AKA arrays on a rename, no winder field's patch depends
 * on the current document — a flat field is simply overwritten. The parameter is
 * kept so the two builders have the same call shape, and so a future field that
 * does need the prior value (an append-rather-than-replace list, say) doesn't
 * force a signature change at every call site.
 */
export function buildWinderPatch(
  entry: WinderEntry,
  acceptedKeys: string[],
  result: WinderEnrichResult,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const key of acceptedKeys) {
    if (key !== WINDER_DESCRIPTION_KEY && !WINDER_ENRICH_FIELDS[key]) continue;
    const coerced = coerceWinderValue(key, getWinderSuggestion(result, key));
    if (coerced === null || coerced === undefined) continue;
    const spec = WINDER_ENRICH_FIELDS[key];
    if (spec?.safeSpecKey) {
      // Dotted path: `updateDoc` creates the nested map when it is absent, so
      // a winder that has never had `safeSpecs` gains one field by field.
      patch[`${SAFE_SPEC_PARENT}.${spec.safeSpecKey}`] = coerced;
      continue;
    }
    if (spec?.displayCaseSpecKey) {
      patch[`${DISPLAY_CASE_SPEC_PARENT}.${spec.displayCaseSpecKey}`] = coerced;
      continue;
    }
    patch[key === WINDER_DESCRIPTION_KEY ? DESCRIPTION_FIELD : key] = coerced;
  }

  return enforceWinderInvariants(entry, patch);
}

/**
 * The two rules a patch must not break — enforced HERE because a bulk apply
 * writes through `updateDoc` and never touches `saveWinder`, where the editor's
 * equivalents live.
 *
 * 1. TPD is one answer in two shapes. Accepting both cells would leave a winder
 *    claiming a settings list AND a continuous range, and every consumer
 *    prefers the range — so the list would silently win nothing and the
 *    document would contradict itself. The range wins and the list is cleared,
 *    matching the documented consumer rule and `resolveTurnsPerDayWrite`.
 * 2. `safeSpecs` exists only for a safe. If the winder is not one — and this
 *    patch is not making it one — the security cells are dropped rather than
 *    written onto a winder whose `isSafe` says they cannot apply.
 * 3. A SAFE DOES NOT WIND. shared-types requires the five winding fields to be
 *    null on an `applianceKind: 'safe'` entry, and a patch is exactly the path
 *    that could break it: the model can answer `applianceKind: 'safe'` and
 *    `turnsPerDay` in the same run, and a reviewer ticking both would write a
 *    self-contradicting document. The kind wins and the winding cells are
 *    dropped — a safe that claims 650 TPD is worse than a safe with a blank
 *    field, because the contradiction propagates into the RN picker and onto an
 *    insurance PDF.
 */
function enforceWinderInvariants(
  entry: WinderEntry,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...patch };

  if (out.turnsPerDayRange && out.turnsPerDay) {
    out.turnsPerDay = null;
  }

  // The flag as it will stand AFTER this patch — an accepted `isSafe` cell
  // outranks what the document says today.
  const effectiveIsSafe =
    typeof out.isSafe === 'boolean' ? out.isSafe : entry.isSafe === true;
  if (!effectiveIsSafe) {
    for (const key of Object.keys(out)) {
      if (key.startsWith(`${SAFE_SPEC_PARENT}.`)) delete out[key];
    }
  }

  // The same rule for the display block, gated on its own flag.
  const effectiveHasDisplayCase =
    typeof out.hasDisplayCase === 'boolean'
      ? out.hasDisplayCase
      : entry.hasDisplayCase === true;
  if (!effectiveHasDisplayCase) {
    for (const key of Object.keys(out)) {
      if (key.startsWith(`${DISPLAY_CASE_SPEC_PARENT}.`)) delete out[key];
    }
  }

  /*
   * The kind as it will stand AFTER this patch, resolved the same way every
   * other surface resolves it.
   *
   * An accepted `applianceKind` cell outranks the document; with no such cell
   * the ENTRY is reclassified through `resolveApplianceKind`, so a pre-B1 safe
   * — `isSafe` with no winding, no `applianceKind` at all — is recognised here
   * too. Reading `entry.applianceKind` directly instead would let a bulk run
   * write turns-per-day onto exactly the legacy safes this PR exists to find.
   */
  const proposed = out.applianceKind;
  const effectiveKind: StorageApplianceKind =
    proposed === 'safe' || proposed === 'winder' || proposed === 'display_case'
      ? proposed
      : resolveApplianceKind(entry);
  // ONLY A WINDER WINDS. A safe and a display case both have no rotor, so a
  // turns-per-day figure for either is a different product or an invention.
  if (effectiveKind !== 'winder') {
    for (const key of WINDER_ONLY_KEYS) delete out[key];
  }
  // A display case is not a safe. The ladder sends anything with a hardened
  // body to `'safe'` before it can reach the display rung, so security specs
  // proposed alongside `applianceKind: 'display_case'` contradict the answer
  // they arrived with.
  if (effectiveKind === 'display_case') {
    delete out.isSafe;
    delete out.safeSpecs;
  }

  return out;
}

/**
 * Coerce a whole enrichWinder result into a partial `WinderEntry`, dropping
 * anything unusable. Used by the single-winder editor to seed its apply diff
 * with values already in stored shape, so the diff compares like with like.
 */
export function winderResultToEntry(result: WinderEnrichResult): Partial<WinderEntry> {
  const out: Record<string, unknown> = {};
  for (const key of suggestedWinderKeys(result)) {
    const coerced = coerceWinderValue(key, getWinderSuggestion(result, key));
    if (coerced === null || coerced === undefined) continue;
    const spec = WINDER_ENRICH_FIELDS[key];
    if (spec?.safeSpecKey) {
      // NESTED here, unlike the patch above: this seeds the editor's apply
      // diff, which compares against a stored `WinderEntry`, and a dotted key
      // would sit beside `safeSpecs` rather than inside it.
      const block = (out[SAFE_SPEC_PARENT] ?? {}) as Record<string, unknown>;
      block[spec.safeSpecKey] = coerced;
      out[SAFE_SPEC_PARENT] = block;
      continue;
    }
    if (spec?.displayCaseSpecKey) {
      const block = (out[DISPLAY_CASE_SPEC_PARENT] ?? {}) as Record<
        string,
        unknown
      >;
      block[spec.displayCaseSpecKey] = coerced;
      out[DISPLAY_CASE_SPEC_PARENT] = block;
      continue;
    }
    out[key === WINDER_DESCRIPTION_KEY ? DESCRIPTION_FIELD : key] = coerced;
  }
  return out as Partial<WinderEntry>;
}
